import React, { useState, useEffect, useMemo } from 'react';
import { OnResultFunction, QrReader } from 'react-qr-reader';
import { useParams } from 'react-router-dom';
import {
  useGetEventCapacityQuery,
  useCountSellerQrCodeMutation,
  useDecodeQrCodeQuery,
  useUpdateUsedOrderItemMutation,
} from '../store';
import { DecodedItemData, DecodedSellerQRData } from '../store/apis/orders';
import QRSubheader from './atoms/QRSubheader';
import QRResponse from './QrResponse';
import SearchByPersonId from './SearchPersonId';
import ButtonGroup from './atoms/ButtonGroup';
import BarcodeScannerScreen from './BarcodeScannerScreen';
import useGetSelectedEvent from '../hooks/useGetSelectedEvent';
import LoadingLottie from './atoms/LoadingLottie';
import ShowtimeTabs from './ShowtimeTabs';
import ListValidatorContent from './ListValidatorContent';

enum Tab {
  CAMERA = 'Cámara',
  SCANNER = 'Scanner',
}

const QRCodeScanner: React.FC = () => {
  const { event, eventLoading } = useGetSelectedEvent();
  const { data: capacity, refetch: refetchCapacity } = useGetEventCapacityQuery({ eventId: event?.id ?? '', businessId: event?.business_id ?? '' }, { skip: !event });
  const [qrData, setQrData] = useState<DecodedItemData | DecodedSellerQRData | null
  >(null);
  const [text, setText] = useState<string | null>(null);
  const [showResponse, setShowResponse] = useState(false);
  const [showConfirmedResponse, setShowConfirmedResponse] = useState(false);
  const [updateUsedOrder] = useUpdateUsedOrderItemMutation();
  const [countSellerQrCode] = useCountSellerQrCodeMutation();
  const [qrResponse, setQrResponse] = useState(<div></div>);
  const { event_id: eventId, business_id: businessId } = useParams();
  const [personId, setPersonId] = useState('');
  const [searchByPersonId, setSearchByPersonId] = useState(false);
  const [showListValidator, setShowListValidator] = useState(false);
  const [isScanning, setIsScanning] = useState(true);
  const [activeTab, setActiveTab] = useState<Tab>(Tab.CAMERA);
  const [selectedCamera, setSelectedCamera] = useState<string>('');
  const [showtimeId, setShowtimeId] = useState<string>('');

  if (!eventId || !businessId) {
    throw new Error('no eventId or businessId');
  }

  const {
    data,
    error,
    isFetching: isDecoding,
  } = useDecodeQrCodeQuery(
    {
      data: text,
      eventId,
      businessId,
      personId,
      hasOrderQr: event?.has_order_qrs ?? false,
    },
    { skip: !text && !personId },
  );

  const cleanData = () => {
    setShowResponse(false);
    setQrResponse(<div>error: recargar</div>);
    setText(null);
    setQrData(null);
    setPersonId('');
    refetchCapacity();
  };

  const handleClose = () => {
    cleanData();
  };

  const handleAcceptModal = () => {
    if (
      (qrData && 'used_at' in qrData && !qrData.used_at)
      || (qrData && 'user_event' in qrData && qrData.user_event)
    ) {
      if (qrData && 'used_at' in qrData && !qrData.used_at) {
        updateUsedOrder({
          businessId,
          eventId: qrData.event_id,
          orderId: qrData.order_id,
          itemId: qrData.item_id,
        });
      } else if (qrData && 'user_event' in qrData && qrData.user_event) {
        countSellerQrCode({
          businessId,
          eventId: qrData.user_event.event_id,
          userEventId: qrData.user_event.id,
        });
      }
      setShowConfirmedResponse(true);
      setTimeout(() => {
        setTimeout(() => {
          setShowConfirmedResponse(false);
          cleanData();
        }, 1000);
      }, 1000);
    }
  };

  const handleAcceptOrderItems = (itemIds: string[]) => {
    if (qrData && 'items' in qrData && 'order_id' in qrData) {
      itemIds.forEach((itemId) => {
        const item = (qrData.items as Array<{ item_id: string }>).find((i) => i.item_id === itemId);
        if (item) {
          updateUsedOrder({
            businessId,
            eventId: qrData.event_id,
            orderId: qrData.order_id,
            itemId: item.item_id,
          });
        }
      });
      setShowConfirmedResponse(true);
      setTimeout(() => {
        setTimeout(() => {
          setShowConfirmedResponse(false);
        }, 1000);
        cleanData();
      }, 1000);
    }
  };

  useEffect(() => {
    if (!text && !personId) {
      return;
    }
    setShowResponse(true);
    if (data) {
      setQrData(data);
    }
    if (error) {
      setQrData(null);
    }
  }, [data, text, personId]);

  useEffect(() => {
    if (qrData && 'valid' in qrData && qrData.valid && 'items' in qrData && qrData.items) {
      setQrResponse(
        <QRResponse
          type="order_qr"
          qrData={qrData}
          onRetry={handleClose}
          onConfirmOrderQR={handleAcceptOrderItems}
          mismatchedShowtime={Array.isArray(qrData.items) && qrData.items.length > 0 && !!qrData.items[0].ticket?.showtime_id && qrData.items[0].ticket?.showtime_id !== showtimeId}
        />,
      );
      return;
    }

    if (qrData && 'valid' in qrData) {
      if (qrData.accepted_on_decode) {
        handleAcceptModal();
        return;
      }

      if (qrData.valid && !qrData.used_at && !qrData.is_expired) {
        setQrResponse(
          <QRResponse
            type="success_unconfirmed"
            qrData={qrData}
            onRetry={handleClose}
            onConfirm={handleAcceptModal}
            mismatchedShowtime={event?.showtimes && event?.showtimes.length > 0 && qrData.showtime_id !== showtimeId}
          />,
        );
        return;
      }
    }

    if (qrData && 'used_at' in qrData && qrData.used_at) {
      setQrResponse(
        <QRResponse type="used" qrData={qrData} onRetry={handleClose} />,
      );
      return;
    }
    if (qrData && 'is_expired' in qrData && qrData.is_expired) {
      setQrResponse(
        <QRResponse type="scan_period_expired" qrData={qrData} onRetry={handleClose} onConfirm={handleAcceptModal} />,
      );
      return;
    }

    if (qrData && 'user_event' in qrData && qrData.user_event) {
      setQrResponse(
        <QRResponse
          type="seller_qr_unconfirmed"
          qrData={qrData}
          onRetry={handleClose}
          onConfirm={handleAcceptModal}
        />,
      );
      return;
    }

    setQrResponse(<div></div>);
  }, [qrData]);

  useEffect(() => {
    if (error && 'status' in error) {
      const errorData = 'data' in error ? error.data as { code?: string } : null;
      if (errorData?.code === 'ScannerNotAllowedToScanTicket') {
        setQrResponse(
          <QRResponse type="wrong_gate" qrData={qrData} onRetry={handleClose} />,
        );
      } else {
        setQrResponse(
          <QRResponse type="error" qrData={qrData} onRetry={handleClose} />,
        );
      }
    }
  }, [error]);

  const handleResult: OnResultFunction = (result) => {
    if (result && isScanning) {
      setText(result?.getText());
      setIsScanning(false);
      setTimeout(() => setIsScanning(true), 2000); // Add a 2-second delay
    }
  };

  const handleIconClick = () => {
    setShowListValidator(false);
    setSearchByPersonId(!searchByPersonId);
  };

  const handleValidatePersonId = (personIdInput: string) => {
    setSearchByPersonId(false);
    setPersonId(personIdInput);
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab as Tab);
  };

  const handleScan = (scannedData: string) => {
    setText(scannedData);
  };

  const handleScanPersonId = (scannedData: string) => {
    setPersonId(scannedData);
  };

  const handleListClick = () => {
    setSearchByPersonId(false);
    setShowListValidator(!showListValidator);
  };

  const cameraConstraints = useMemo(() => {
    const constraints: MediaTrackConstraints = {
      facingMode: 'environment',
    };

    // Only add deviceId if we have a selected camera
    if (selectedCamera) {
      constraints.deviceId = { exact: selectedCamera };
    }

    return constraints;
  }, [selectedCamera]);

  if (eventLoading) {
    return <LoadingLottie />;
  }

  return (
    <div className="mb-12">
      <div className="flex w-full shadow-sm">
        <QRSubheader
          onSearchClick={handleIconClick}
          onCameraClick={setSelectedCamera}
          onListClick={handleListClick}
          capacity={capacity?.used_tickets}
          event={event}
        />
      </div>
      {event?.has_showtimes && event && (
        <div className="my-2">
          <ShowtimeTabs
            event={event}
            onTabChange={setShowtimeId}
            selectedTab={showtimeId}
            disabled={showResponse}
          />
        </div>
      )}
      {false && <div className="mx-auto max-w-md">
        <ButtonGroup
          buttons={[Tab.CAMERA, Tab.SCANNER]}
          defaultButton={Tab.CAMERA}
          onButtonChange={handleTabChange}
        />
      </div>}
      {showConfirmedResponse && (
        <div>
          <QRResponse
            type={
              qrData && 'user_event' in qrData
                ? 'seller_qr_confirmed'
                : 'success_confirmed'
            }
            qrData={qrData}
            onRetry={handleClose}
          />
        </div>
      )}
      {showListValidator && (
        <div className="my-6 mx-2 lg:mx-6">
          <ListValidatorContent listId={event?.list?.id || ''} />
        </div>
      )}
      {searchByPersonId && (
        <div className="glass-surface m-6 text-text-primary">
          <SearchByPersonId onValidate={handleValidatePersonId} />
        </div>
      )}

      <div hidden={showResponse || showConfirmedResponse || searchByPersonId || showListValidator}>
        {activeTab === Tab.CAMERA
          && <div hidden={showResponse} className="w-full mx-auto max-w-lg px-4">
            <div className="relative w-full aspect-[9/16] bg-surface-tertiary rounded-lg overflow-hidden mt-4">
              <QrReader
                key={`camera-${selectedCamera}`}
                onResult={handleResult}
                constraints={cameraConstraints}
                containerStyle={{ width: '100%', height: '100%' }}
                videoContainerStyle={{
                  width: '100%',
                  height: '100%',
                }}
                videoStyle={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            </div>
          </div>}
        {(activeTab === Tab.SCANNER && !qrData) && <BarcodeScannerScreen onScanQr={handleScan} onScanPersonId={handleScanPersonId} />}
        {showResponse && qrResponse}
      </div>
      {showResponse && !isDecoding && !showConfirmedResponse && (
        <div className="w-full flex items-center justify-center">
          {qrResponse}
        </div>
      )}
      {showResponse && isDecoding && (
        <div className="h-screen w-full flex items-center justify-center">
          <div
            className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
            role="status"
          >
            <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">
              Loading...
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default QRCodeScanner;
